/**
 * Request Store Selectors
 *
 * Contains all selector functions for the request store.
 * Selectors provide type-safe access to the store state.
 *
 * @module @features/requests/stores/request/requestSelectors
 */

import type { RequestState } from './requestTypes';
import type {
    Request,
    RequestStatus,
    RequestSource,
} from '@features/requests/types';

/**
 * Selector for all requests in the store
 * @param state - The request store state
 * @returns Array of all requests
 */
export const selectRequests = (state: RequestState): Request[] =>
    state.requests;

/**
 * Selector for the currently selected request ID
 * @param state - The request store state
 * @returns The ID of the selected request or null
 */
export const selectSelectedRequestId = (state: RequestState): string | null =>
    state.selectedRequestId;

/**
 * Selector for loading state
 * @param state - The request store state
 * @returns Boolean indicating if data is being loaded
 */
export const selectIsLoading = (state: RequestState): boolean =>
    state.isLoading;

/**
 * Selector for error state
 * @param state - The request store state
 * @returns Error message string or null
 */
export const selectError = (state: RequestState): string | null => state.error;

/**
 * Selector for selected request
 * @param state - The request store state
 * @returns The selected request or null
 */
export const selectSelectedRequest = (state: RequestState): Request | null => {
    const selectedId = selectSelectedRequestId(state);
    if (!selectedId) return null;
    return selectRequests(state).find((r) => r.id === selectedId) ?? null;
};

/**
 * Selector for requests filtered by status
 * @param state - The request store state
 * @param status - The status to filter by
 * @returns Array of requests with the specified status
 */
export const selectRequestsByStatus = (
    state: RequestState,
    status: RequestStatus,
): Request[] => selectRequests(state).filter((r) => r.status === status);

/**
 * Selector for requests filtered by type
 * @param state - The request store state
 * @param type - The type to filter by
 * @returns Array of requests with the specified type
 */
export const selectRequestsByType = (
    state: RequestState,
    type: RequestSource,
): Request[] => selectRequests(state).filter((r) => r.type === type);
